import type { MouseEvent } from "react";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { indigo, grey, amber } from "@mui/material/colors";
import { Stack } from "@mui/system";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  SubmissionStatusChipWithPopover,
  getStatusDisplay as getChipStatusDisplay,
  getEncampStatusDisplay,
} from "components/Submission/SubmissionStatusChipWithPopover";
import CopyableTypography from "components/Typography/CopyableTypography";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  WasteSubmissionsForStaffQuery,
  SubmissionEncampStatus,
  SubmissionStatus,
  SubmissionType,
  VirtualEventStatus,
  Frequency,
  SubmissionMaterializationType,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef, FilterKey } from "hooks/useOmnisearchDatagrid";
import { Link } from "react-router-dom";
import { formatDateLikeForDisplay, prettyPrintDateTimeMed } from "util/dates";
import { buildWasteFacilityUrl } from "util/wasteFacilityUrl";

import { getMaterializationType } from "./utils";

export type Row = WasteSubmissionsForStaffQuery["wasteSubmissionsForStaff"]["items"][number];

function formatEncampFilterLabel(value: string): string {
  const compoundIdx = value.indexOf("__");
  if (compoundIdx !== -1) {
    const parentKey = value.slice(0, compoundIdx);
    const encampKey = value.slice(compoundIdx + 2);
    return `${getChipStatusDisplay(parentKey)} - ${getEncampStatusDisplay(encampKey)}`;
  }
  const parentEntry = Object.entries(VALID_ENCAMP_STATUSES).find(([, s]) =>
    s?.includes(value as SubmissionEncampStatus)
  );
  if (parentEntry) {
    return `${getChipStatusDisplay(parentEntry[0])} - ${getEncampStatusDisplay(value)}`;
  }
  return getEncampStatusDisplay(value);
}

const GENERATOR_STATUS_CONFIG: Record<string, { label: string; color: string; textColor: string }> =
  {
    LARGE_QUANTITY_GENERATOR: {
      label: "LQG",
      color: indigo[600],
      textColor: "#fff",
    },
    SMALL_QUANTITY_GENERATOR: {
      label: "SQG",
      color: indigo[300],
      textColor: "#000",
    },
    VERY_SMALL_QUANTITY_GENERATOR: {
      label: "VSQG",
      color: indigo[200],
      textColor: "#000",
    },
    NONE: {
      label: "None",
      color: grey[300],
      textColor: "#000",
    },
  };

export function getRowHref(row: Row): string | null {
  if (!row.taskId || !row.deadline) return null;
  const isMaterializedInstance = row.taskFrequency === Frequency.Once;
  if (isMaterializedInstance && row.submissionId) {
    // Stepper (8700-12 data-entry) submissions always open in the unified
    // stepper — for staff and customers alike — rather than the document-review
    // staff view. Peer-review claiming lives on the stepper's Review step, so
    // this also covers the unassigned-peer-review case for stepper submissions.
    if (row.tenantId && row.submission?.usesStepper) {
      return `/o/${row.tenantId}/waste/submissions/${row.submissionId}/stepper`;
    }
    const isUnassignedPeerReview =
      row.submissionStatus === SubmissionStatus.EncampInitialReview &&
      row.submission?.peerReviewRequired === true &&
      !row.staffReviewer;
    if (isUnassignedPeerReview && row.tenantId) {
      return `/o/${row.tenantId}/waste/submissions/${row.submissionId}/edit`;
    }
    return `/staff/fulfillment/waste/submissions/${row.submissionId}`;
  }
  if (!row.tenantId) return null;
  const dateOnly = row.deadline.split("T")[0];
  return `/o/${row.tenantId}/waste/submissions/create?taskId=${row.taskId}&startDate=${dateOnly}`;
}

function getFacilityDetailHref(row: Row): string | null {
  const primaryFacility = row.facilities?.[0];
  if (!row.tenantId || !primaryFacility?.id) return null;
  return buildWasteFacilityUrl(row.tenantId, primaryFacility.id);
}

function getFulfillmentDetailHref(row: Row): string | null {
  if (row.taskFrequency !== Frequency.Once || !row.submissionId) return null;
  return `/staff/fulfillment/waste/submissions/${row.submissionId}`;
}

function getCustomerSubmissionHref(row: Row): string | null {
  if (row.taskFrequency !== Frequency.Once || !row.submissionId || !row.tenantId) {
    return null;
  }
  // Stepper (8700-12 data-entry) submissions are edited in the stepper, not the
  // document-review edit form.
  if (row.submission?.usesStepper) {
    return `/o/${row.tenantId}/waste/submissions/${row.submissionId}/stepper`;
  }
  return `/o/${row.tenantId}/waste/submissions/${row.submissionId}/edit`;
}

function getStatusDisplay(status: string): string {
  switch (status) {
    case "NOT_STARTED":
      return "Not Started";
    case "IN_PROGRESS":
      return "In Progress";
    case "ENCAMP_INITIAL_REVIEW":
      return "Initial Review";
    case "IN_REVIEW":
      return "In Review";
    case "PROCESSING_SUBMISSION":
      return "Processing";
    case "FILING_COMPLETE":
      return "Complete";
    case "NOT_REPORTING":
      return "Not Reporting";
    default:
      return status;
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case "NOT_STARTED":
      return "#f57c00";
    case "IN_PROGRESS":
      return "#1976d2";
    case "ENCAMP_INITIAL_REVIEW":
      return "#9c27b0";
    case "IN_REVIEW":
      return "#0288d1";
    case "PROCESSING_SUBMISSION":
      return "#7b1fa2";
    case "FILING_COMPLETE":
      return "#388e3c";
    case "NOT_REPORTING":
      return "#757575";
    default:
      return "#757575";
  }
}

function isOverdue(row: Row): boolean {
  return row.virtualEventStatus === VirtualEventStatus.Overdue;
}

export const COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    field: "tenantName",
    headerName: "Organization",
    width: 180,
    linkable: false,
    renderCell: ({ row }) => {
      const href = getRowHref(row);
      if (!href) {
        return (
          <Tooltip title={row.tenantName}>
            <Typography noWrap variant="body2">
              {row.tenantName}
            </Typography>
          </Tooltip>
        );
      }
      return (
        <Tooltip title={row.tenantName}>
          <Typography
            noWrap
            variant="body2"
            component={Link}
            to={href}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.tenantName}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "facilityName",
    headerName: "Facility",
    width: 180,
    linkable: false,
    renderCell: ({ row }) => {
      const primaryFacility = row.facilities?.[0];
      const facilityDetailHref = getFacilityDetailHref(row);
      if (!facilityDetailHref) {
        return (
          <Tooltip title={`${row.facilityName ?? ""}\n${primaryFacility?.state ?? ""}`}>
            <Typography noWrap variant="body2">
              {row.facilityName ?? "—"}
            </Typography>
          </Tooltip>
        );
      }
      return (
        <Tooltip title={`${row.facilityName ?? ""}\n${primaryFacility?.state ?? ""}`}>
          <Typography
            noWrap
            variant="body2"
            component={Link}
            to={facilityDetailHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.facilityName ?? "—"}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "epaIds",
    headerName: "EPA ID",
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const epaIds = row.epaIds ?? [];
      if (epaIds.length === 0) return "—";
      if (epaIds.length === 1) {
        return (
          <CopyableTypography
            text={epaIds[0]}
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          />
        );
      }
      return (
        <CopyableTypography
          text={epaIds.join(", ")}
          displayText={`${epaIds[0]}...`}
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      );
    },
  },
  {
    field: "state",
    headerName: "State/Territory",
    width: 70,
    filterable: false,
  },
  {
    field: "title",
    headerName: "Title",
    width: 180,
    filterable: false,
    renderCell: ({ row }) => (
      <Tooltip title={row.title}>
        <Typography noWrap variant="body2">
          {row.title}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "submissionType",
    headerName: "Type",
    width: 150,
    filterable: false,
    renderCell: ({ row }) => (
      <Tooltip title={row.submissionType}>
        <Typography noWrap variant="body2">
          {prettyPrintEnumValue(row.submissionType)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "submissionStatus",
    headerName: "Status",
    width: 130,
    filterable: false,
    linkable: false,
    renderCell: ({ row }) => (
      <SubmissionStatusChipWithPopover
        submissionId={row.submissionId}
        submissionStatus={row.submissionStatus}
        encampStatus={row.submissionEncampStatus}
        isStaff={true}
      />
    ),
  },
  {
    field: "materializationType",
    headerName: "",
    width: 40,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    linkable: false,
    renderCell: ({ row }) => {
      const materializationType = getMaterializationType(row);
      if (materializationType === SubmissionMaterializationType.VirtualEvent) {
        const requirementsLink = `/staff/requirements?omnisearch=taskId:${row.taskId}`;
        return (
          <Tooltip title="Virtual event - click to view requirement">
            <IconButton
              component={Link}
              to={requirementsLink}
              target="_blank"
              size="small"
              sx={{ p: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloudQueueIcon fontSize="small" sx={{ color: "text.disabled" }} />
            </IconButton>
          </Tooltip>
        );
      }
      if (materializationType === SubmissionMaterializationType.OneOff) {
        return (
          <Tooltip title="One-off submission - no linked requirement">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
              }}
            >
              <PostAddIcon fontSize="small" sx={{ color: amber[700], opacity: 0.8 }} />
            </Box>
          </Tooltip>
        );
      }
      const requirementsLink = `/staff/requirements?omnisearch=seriesId:${row.seriesId}`;
      return (
        <Tooltip title="Materialized instance - click to view requirement">
          <IconButton
            component={Link}
            to={requirementsLink}
            target="_blank"
            size="small"
            sx={{ p: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CheckCircleOutlineIcon fontSize="small" sx={{ color: "success.main", opacity: 0.7 }} />
          </IconButton>
        </Tooltip>
      );
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 96,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: ({ row }) => {
      const customerHref = getCustomerSubmissionHref(row);
      const fulfillmentDetailHref = getFulfillmentDetailHref(row);
      return (
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip
            title={customerHref ? "Open customer submission" : "Customer submission unavailable"}
          >
            <span>
              <IconButton
                size="small"
                component={customerHref ? Link : "button"}
                to={customerHref ?? undefined}
                target={customerHref ? "_blank" : undefined}
                rel={customerHref ? "noopener noreferrer" : undefined}
                disabled={!customerHref}
                onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              fulfillmentDetailHref ? "Open fulfillment detail" : "Fulfillment detail unavailable"
            }
          >
            <span>
              <IconButton
                size="small"
                component={fulfillmentDetailHref ? Link : "button"}
                to={fulfillmentDetailHref ?? undefined}
                disabled={!fulfillmentDetailHref}
                onClick={(e: MouseEvent<HTMLElement>) => e.stopPropagation()}
              >
                <DescriptionOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    },
  },
  {
    field: "waitingOn",
    headerName: "Customer Next Action",
    width: 160,
    sortable: false,
    renderCell: ({ row }) => {
      const waitingOn = row.waitingOn ?? [];
      if (waitingOn.length === 0) {
        const status = row.submissionStatus;
        if (status === "NOT_STARTED" || status === "IN_PROGRESS") {
          return (
            <Typography noWrap variant="body2" color="text.secondary">
              Encamp
            </Typography>
          );
        }
        if (status === "PROCESSING_SUBMISSION") {
          return (
            <Typography noWrap variant="body2" color="text.secondary">
              Ready to file
            </Typography>
          );
        }
        return "—";
      }
      if (waitingOn.length === 1) {
        const user = waitingOn[0];
        const name = user.firstName
          ? `${user.firstName} ${user.lastName ?? ""}`.trim()
          : user.email;
        return (
          <Tooltip title={user.isSigner ? "Signer" : "Reviewer"}>
            <Typography noWrap variant="body2">
              {name}
              {user.isSigner && " (Sign)"}
            </Typography>
          </Tooltip>
        );
      }
      return (
        <Tooltip
          title={waitingOn
            .map((u) => (u.firstName ? `${u.firstName} ${u.lastName}` : u.email))
            .join(", ")}
        >
          <Typography noWrap variant="body2">
            {waitingOn.length} reviewers
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "deadline",
    headerName: "Due Date",
    width: 110,
    renderCell: ({ row }) => (
      <Typography
        noWrap
        variant="body2"
        color={isOverdue(row) ? "error" : "text.primary"}
        fontWeight={isOverdue(row) ? 600 : 400}
      >
        {row.deadline ? formatDateLikeForDisplay(row.deadline) : "—"}
      </Typography>
    ),
  },
  {
    field: "ownerName",
    headerName: "Encamp Assignee",
    width: 140,
    renderCell: ({ row }) => (
      <Tooltip title={row.ownerEmail ?? ""}>
        <Typography noWrap variant="body2">
          {row.ownerName || row.ownerEmail || "—"}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "staffReviewer",
    headerName: "Peer Reviewer",
    width: 180,
    renderCell: ({ row }) => {
      const reviewer = row.staffReviewer;
      if (!reviewer) return "—";
      const displayName =
        reviewer.firstName || reviewer.lastName
          ? `${reviewer.firstName ?? ""} ${reviewer.lastName ?? ""}`.trim()
          : (reviewer.email ?? "—");
      return (
        <Tooltip title={reviewer.email ?? ""}>
          <Typography noWrap variant="body2">
            {displayName}
            {reviewer.isSigner ? " (Sign)" : ""}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "generatorStatus",
    headerName: "Generator",
    width: 90,
    renderCell: ({ row }) => {
      if (!row.generatorStatus) return "—";
      const config = GENERATOR_STATUS_CONFIG[row.generatorStatus];
      if (!config) {
        return (
          <Typography noWrap variant="body2">
            {row.generatorStatus}
          </Typography>
        );
      }
      return (
        <Chip
          label={config.label}
          size="small"
          sx={{
            backgroundColor: config.color,
            color: config.textColor,
            fontWeight: 500,
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      );
    },
  },
  {
    field: "lastInternalComment",
    headerName: "Internal Comment",
    width: 250,
    sortable: false,
    renderCell: ({ row }) => {
      if (!row.lastInternalComment) return "—";
      return (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {row.lastInternalCommentByName ?? "Unknown"} •{" "}
                {row.lastInternalCommentAt ? prettyPrintDateTimeMed(row.lastInternalCommentAt) : ""}
              </Typography>
              <Typography variant="body2">{row.lastInternalComment}</Typography>
            </Stack>
          }
        >
          <Typography noWrap variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              {row.lastInternalCommentByName ?? "Unknown"}:
            </Typography>{" "}
            {row.lastInternalComment}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "lastComment",
    headerName: "Customer Comment",
    width: 250,
    sortable: false,
    renderCell: ({ row }) => {
      if (!row.lastComment) return "—";
      return (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                {row.lastCommentByName ?? "Unknown"} •{" "}
                {row.lastCommentAt ? prettyPrintDateTimeMed(row.lastCommentAt) : ""}
              </Typography>
              <Typography variant="body2">{row.lastComment}</Typography>
            </Stack>
          }
        >
          <Typography noWrap variant="body2">
            <Typography component="span" variant="body2" color="text.secondary">
              {row.lastCommentByName ?? "Unknown"}:
            </Typography>{" "}
            {row.lastComment}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "lastActivity",
    headerName: "Last Activity",
    width: 140,
    renderCell: ({ row }) => (
      <Typography noWrap variant="body2">
        {row.lastActivity ? prettyPrintDateTimeMed(row.lastActivity) : "—"}
      </Typography>
    ),
  },
];

export const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Organization & Facility",
    fields: ["tenant", "facility", "epaId"],
  },
  {
    name: "Location",
    fields: ["state"],
  },
  {
    name: "Submission",
    fields: ["submissionType", "status", "encampStatus", "waitingOn", "dueDate"],
  },
  {
    name: "Generator Status",
    fields: ["generatorStatus"],
  },
  {
    name: "Staff Assignment",
    fields: ["staffAssignee", "peerReviewer", "customerRejected"],
  },
];

// Status, State, and Generator Status are owned by the top-row controls in index.tsx,
// and the assignedToMe/needsMyAttention/peerReviewQueue keys are owned by the "View by"
// selector there. Declaring any of them here too would create a second write path: the
// status/state controls serialize differently, and the View-by selector is mutually
// exclusive, so independent popover booleans could reach states it can't represent.
export const ADDITIONAL_FILTER_COLUMNS: FilterKey[] = [
  {
    header: "Organization",
    key: "tenant",
    filterKeyType: "multipleTenant",
  },
  {
    header: "Facility",
    key: "facility",
    filterKeyType: "facility",
  },
  {
    header: "Submission Type",
    key: "submissionType",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(SubmissionType),
  },
  { header: "EPA ID", key: "epaId" },
  {
    header: "Encamp Status",
    key: "encampStatus",
    filterKeyType: "enumAutocomplete",
    enumValues: (() => {
      const values: string[] = [];
      for (const encampVal of Object.values(SubmissionEncampStatus)) {
        const parents = Object.entries(VALID_ENCAMP_STATUSES).filter(([, s]) =>
          s?.includes(encampVal)
        );
        if (parents.length > 1) {
          for (const [parentKey] of parents) {
            values.push(`${parentKey}__${encampVal}`);
          }
        } else {
          values.push(encampVal);
        }
      }
      return values.sort((a, b) => {
        const labelA = formatEncampFilterLabel(a)
          .replace(/[^\p{L}\p{N}\s-]/gu, "")
          .trim();
        const labelB = formatEncampFilterLabel(b)
          .replace(/[^\p{L}\p{N}\s-]/gu, "")
          .trim();
        return labelA.localeCompare(labelB);
      });
    })(),
    enumPresentationFunction: formatEncampFilterLabel,
  },
  {
    header: "Encamp Assignee",
    key: "staffAssignee",
    nullCheckboxLabel: "No Assignee",
  },
  {
    header: "Peer Reviewer",
    key: "peerReviewer",
    nullCheckboxLabel: "No Peer Reviewer",
  },
  {
    header: "Customer Rejected",
    key: "customerRejected",
    filterKeyType: "boolean",
  },
  {
    header: "Materialization Type",
    key: "materializationType",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(SubmissionMaterializationType),
  },
];

// These columns auto-generate free-text filters (useOmnisearchDatagrid keys them by
// col.field) that the backend never parses — silent no-ops. Excluding the fields here
// stops the dead filter entries from being generated; the columns still display (ENG-15798).
export const EXCLUDE_FILTER_COLUMNS = [
  "tenantName",
  "facilityName",
  "ownerName",
  "lastInternalComment",
  "lastComment",
  "lastActivity",
];

// Omnisearch keys owned by the "View by" selector (see the comment above
// ADDITIONAL_FILTER_COLUMNS). They're intentionally NOT popover filter columns,
// so they must be listed as preservedFilterKeys on the grid — otherwise
// filterValidKeys strips them when a saved filter is restored (ENG-15764).
export const STAFF_VIEW_BY_FILTER_KEYS = ["assignedToMe", "needsMyAttention", "peerReviewQueue"];
